import React, { useState, useEffect } from "react";
import { ClassRoom, Quiz } from "../types";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { useData } from "../context/DataContext";
import { formatDate } from "../utils/fileUtils";
import ContributionGraph from '../components/ContributionGraph';
import { FaVideo, FaPlay } from 'react-icons/fa';

// Component trang chủ
const HomePage: React.FC = () => {
  const { publicClasses: cachedPublicClasses, isDataLoaded } = useData();
  const [publicClasses, setPublicClasses] = useState<ClassRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalClasses, setTotalClasses] = useState(0);
  const [totalQuizzes, setTotalQuizzes] = useState(0);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mousePosition1, setMousePosition1] = useState({ x: 0, y: 0 });
  const [mousePosition2, setMousePosition2] = useState({ x: 0, y: 0 });
  const [streak, setStreak] = useState(0);
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [expandedClasses, setExpandedClasses] = useState<Record<string, boolean>>({});

  // Lazy loading state for quizzes
  const [classQuizzes, setClassQuizzes] = useState<Record<string, Quiz[]>>({});
  const [loadingQuizzes, setLoadingQuizzes] = useState<Record<string, boolean>>({});

  const toggleClassExpansion = async (classId: string) => {
    setExpandedClasses((prev) => ({
      ...prev,
      [classId]: !prev[classId],
    }));

    // Lazy load quizzes when expanding
    if (!expandedClasses[classId] && !classQuizzes[classId]) {
      setLoadingQuizzes(prev => ({ ...prev, [classId]: true }));
      try {
        const { getToken } = await import("../utils/auth");
        const token = getToken();
        if (token) {
          const { QuizzesAPI } = await import("../utils/api");
          const quizzes = await QuizzesAPI.byClass(classId, token);
          const publishedQuizzes = quizzes.filter((q: any) => q.published === true);
          setClassQuizzes(prev => ({ ...prev, [classId]: publishedQuizzes }));
        }
      } catch (error) {
        console.error("Error loading quizzes:", error);
      } finally {
        setLoadingQuizzes(prev => ({ ...prev, [classId]: false }));
      }
    }
  };


  // Hàm xử lý di chuyển chuột cho ảnh 1 (Kho tài liệu)
  const handleMouseMove1 = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const mouseX = e.clientX - centerX;
    const mouseY = e.clientY - centerY;

    setMousePosition1({ x: mouseX, y: mouseY });
  };

  const handleMouseLeave1 = () => {
    setMousePosition1({ x: 0, y: 0 });
  };

  // Hàm xử lý di chuyển chuột cho ảnh 2 (HoanBuCon)
  const handleMouseMove2 = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const mouseX = e.clientX - centerX;
    const mouseY = e.clientY - centerY;

    setMousePosition2({ x: mouseX, y: mouseY });
  };

  const handleMouseLeave2 = () => {
    setMousePosition2({ x: 0, y: 0 });
  };

  const [activityLog, setActivityLog] = useState<Record<string, number>>({});

  // ... (previous state)

  // Fetch public classes data & profile stats
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const { getToken } = await import("../utils/auth");
        const token = getToken();

        if (!token) {
          setIsLoggedIn(false);
          setLoading(false);
          return;
        }

        setIsLoggedIn(true);
        const { ClassesAPI, QuizzesAPI, AuthAPI, StatsAPI } = await import("../utils/api");

        // Fetch user info for streak
        try {
          const userRes = await AuthAPI.me(token);
          setStreak(userRes.user.streak || 0);

          // Fetch full profile stats including activity log
          const stats = await StatsAPI.getProfileStats(token);
          if (stats.activityLog) {
            setActivityLog(stats.activityLog);
          }
        } catch (e) {
          console.error("Error fetching user stats", e);
        }

        // Optimized Data Loading using Context
        if (isDataLoaded && cachedPublicClasses.length > 0) {
          setPublicClasses(cachedPublicClasses);
          setTotalClasses(cachedPublicClasses.length);

          // Populate class quizzes from cache for instant expansion
          const quizzesMap: Record<string, Quiz[]> = {};
          cachedPublicClasses.forEach((cls: any) => {
            if (cls.quizzes && cls.quizzes.length > 0) {
              quizzesMap[cls.id] = cls.quizzes;
            }
          });
          setClassQuizzes(prev => ({ ...prev, ...quizzesMap }));

          const totalQuizzesCount = cachedPublicClasses.reduce((sum: number, cls: any) =>
            sum + (cls.quizzes?.length || cls.quizCount || 0), 0
          );
          setTotalQuizzes(totalQuizzesCount);
          setLoading(false);
          return;
        }

        // Fetch public classes (Fallback)
        const publicClassesData = await ClassesAPI.listPublic(token);

        // Process public classes WITHOUT loading quizzes (lazy load later)
        const processedClasses: ClassRoom[] = publicClassesData.map((cls: any) => ({
          id: cls.id,
          name: cls.name,
          description: cls.description,
          quizzes: [], // Will be loaded lazily when user expands
          createdAt: new Date(cls.createdAt),
          updatedAt: cls.updatedAt ? new Date(cls.updatedAt) : undefined,
        } as unknown as ClassRoom));

        setPublicClasses(processedClasses);
        setTotalClasses(processedClasses.length);

        // Calculate total quizzes from backend data if available
        const totalQuizzesCount = publicClassesData.reduce((sum: number, cls: any) =>
          sum + (cls.quizCount || 0), 0
        );
        setTotalQuizzes(totalQuizzesCount);
      } catch (error) {
        console.error("Error fetching public classes:", error);
        setIsLoggedIn(false);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isDataLoaded, cachedPublicClasses]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".dropdown-container")) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      {/* Hero Section - Full Width */}
      <div className="relative overflow-hidden group bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 dark:from-blue-900 dark:via-slate-900 dark:to-slate-950 shadow-2xl animate-slideDownIn">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl mix-blend-overlay"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl mix-blend-overlay"></div>

        {/* Overlay pattern */}
        <div className="absolute inset-0 opacity-[0.08] bg-[radial-gradient(circle_at_1px_1px,_#fff_1px,_transparent_0)] bg-[size:24px_24px] pointer-events-none"></div>

        {/* Shimmer Effect */}
        <div
          className="
              absolute inset-0
              opacity-20
              bg-gradient-to-r from-transparent via-white/40 to-transparent
              blur-[2px]
              animate-[shimmer_3s_ease-in-out_infinite]
              [mask-image:linear-gradient(to_right,transparent_0%,black_20%,black_80%,transparent_100%)]
              mix-blend-overlay
              pointer-events-none
              "
        ></div>

        <div className="max-w-screen-2xl mx-auto p-6 sm:p-8 lg:p-10">
          <div className="relative z-10 flex flex-col xl:flex-row gap-8 items-stretch xl:items-start xl:justify-between">
            {/* Left: Branding */}
            <div className="space-y-4 xl:max-w-2xl">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-mono font-medium text-white mb-4 tracking-tight">
                <div className="relative inline-block logo-main-wrapper">
                  <span className="logo-text text-3xl sm:text-4xl lg:text-5xl relative z-10">
                    CNTT K23M
                  </span>
                  <span className="logo-text text-3xl sm:text-4xl lg:text-5xl logo-glitch-layer logo-glitch-layer-1" aria-hidden="true">
                    CNTT K23M
                  </span>
                  <span className="logo-text text-3xl sm:text-4xl lg:text-5xl logo-glitch-layer logo-glitch-layer-2" aria-hidden="true">
                    CNTT K23M
                  </span>
                </div>
              </h1>
              <div className="text-xl sm:text-2xl font-mono text-blue-100 flex items-center gap-2 opacity-90">
                <span>Ngại gì khó, sợ gì sai</span>
                <span className="animate-bounce">🐧</span>
              </div>

              <p className="text-lg text-blue-50 font-mono border-l-4 border-white/50 pl-4 py-1 text-shadow-sm">
                Nền tảng học tập trực tuyến
              </p>

              <div className="flex flex-col gap-3 mt-6">
                <div className="flex items-center gap-3 text-white font-mono text-sm group/item">
                  <div className="w-2 h-2 rounded-full bg-cyan-300 shadow-[0_0_10px_#67e8f9] group-hover/item:scale-125 transition-transform"></div>
                  <span className="opacity-90">Cực chất 👻 🔥</span>
                </div>
                <div className="flex items-center gap-3 text-white font-mono text-sm group/item">
                  <div className="w-2 h-2 rounded-full bg-cyan-300 shadow-[0_0_10px_#67e8f9] group-hover/item:scale-125 transition-transform"></div>
                  <span className="opacity-90">Cực đoàn kết 🤝</span>
                </div>
                <div className="flex items-center gap-3 text-white font-mono text-sm group/item">
                  <div className="w-2 h-2 rounded-full bg-cyan-300 shadow-[0_0_10px_#67e8f9] group-hover/item:scale-125 transition-transform"></div>
                  <span className="opacity-90">Cực chăm học 📚</span>
                </div>
              </div>
            </div>

            {/* Right: Contribution Graph & Stats */}
            <div>
              <div className="bg-black/20 backdrop-blur-md rounded-xl border border-white/10 p-4 shadow-inner text-white w-full xl:w-fit">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
                  <h3 className="text-sm font-bold text-blue-100 uppercase tracking-wider flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16"><path d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0ZM1.5 8a6.5 6.5 0 1 0 13 0 6.5 6.5 0 0 0-13 0Zm4.879-2.773 4.264 2.559a.25.25 0 0 1 0 .428l-4.264 2.559A.25.25 0 0 1 6 10.559V5.442a.25.25 0 0 1 .379-.215Z"></path></svg>
                    Hoạt động làm bài
                  </h3>
                  <div className="flex flex-wrap gap-4 text-xs font-mono text-blue-200">
                    <span>{totalClasses} Classes</span>
                    <span>{totalQuizzes} Quizzes</span>
                    <span className="text-orange-400 font-bold">{streak} Streak 🔥</span>
                  </div>
                </div>

                {/* The Graph */}
                <div className="w-full overflow-x-auto custom-scrollbar pb-2">
                  <ContributionGraph data={activityLog} />
                </div>


              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8 animate-fadeIn">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Left Section - Main Content */}
          <div className="lg:w-[70%]">
            <div className="mb-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Lớp học công khai
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Khám phá và tham gia các lớp học được chia sẻ công khai
              </p>
            </div>

            {loading ? (
              <div className="py-16 flex items-center justify-center">
                {(() => {
                  const SpinnerLoading = require("../components/SpinnerLoading").default;
                  return <div style={{ transform: 'scale(0.435)' }}><SpinnerLoading /></div>;
                })()}
              </div>
            ) : publicClasses.length === 0 ? (
              // Empty state
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 mb-4">
                  <svg
                    className="w-8 h-8 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {isLoggedIn
                    ? "Chưa có lớp học công khai"
                    : "Vui lòng ĐĂNG NHẬP để tham gia lớp học"}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {isLoggedIn
                    ? "Hãy quay lại sau để khám phá các lớp học mới"
                    : "Bạn cần đăng nhập để được cấp quyền truy cập vào các tính năng của website"}
                </p>
              </div>
            ) : (
              // Danh sách lớp học
              <div className="space-y-4">
                {publicClasses.map((classRoom, index) => (
                  <div
                    key={classRoom.id}
                    className="group card p-4 sm:p-6 hover:shadow-2xl hover:scale-[1.01] transition-all duration-300 border-l-4 border-l-gray-300 dark:border-l-gray-600 hover:border-l-primary-500 dark:hover:border-l-primary-500 animate-slideUpIn anim-delay-100"
                    style={{ animationDelay: `${(index % 5) * 0.1}s` }}
                  >
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-4 gap-4">
                      <div className="flex-1">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="flex-shrink-0 w-16 h-16 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white font-bold text-xl shadow-lg">
                            {classRoom.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                              {classRoom.name}
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                              {classRoom.description}
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                          <span className="inline-flex items-center gap-1.5">
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                            {formatDate(classRoom.createdAt)}
                          </span>
                          <span className="text-gray-300 dark:text-gray-600">
                            •
                          </span>
                          <span className="text-gray-300 dark:text-gray-600">
                            •
                          </span>
                          <div className="relative dropdown-container">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenDropdown(openDropdown === classRoom.id ? null : classRoom.id);
                              }}
                              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors ${openDropdown === classRoom.id
                                ? "text-blue-600 dark:text-blue-400"
                                : "text-gray-500 dark:text-gray-400"
                                }`}
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                              {classQuizzes[classRoom.id]?.length || 0} bài kiểm tra
                              <div className={`w-5 h-5 flex items-center justify-center rounded transition-all duration-200 mt-[1px] ${openDropdown === classRoom.id
                                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600'
                                : 'rotate-180 bg-transparent text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                                }`}>
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </div>
                            </button>

                            {/* Dropdown Menu (Popup) */}
                            {openDropdown === classRoom.id && classQuizzes[classRoom.id] && classQuizzes[classRoom.id].length > 0 && (
                              <div className="absolute top-full left-0 mt-2 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden animate-slideUpFade">
                                <div className="bg-blue-600 px-4 py-3">
                                  <p className="text-sm font-bold text-white">
                                    Chọn bài kiểm tra
                                  </p>
                                </div>
                                <div className="p-2 max-h-64 overflow-y-auto custom-scrollbar">
                                  {(classQuizzes[classRoom.id] as Quiz[]).map((quiz, idx) => (
                                    <button
                                      key={quiz.id}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/quiz/${quiz.id}`, {
                                          state: { className: classRoom.name },
                                        });
                                        setOpenDropdown(null);
                                      }}
                                      className="w-full text-left px-3 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg transition-colors duration-200 group flex items-start gap-3"
                                    >
                                      <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold text-sm">
                                        {idx + 1}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors line-clamp-1">
                                          {quiz.title}
                                        </div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                          {quiz.questionCount ?? quiz.questions?.length ?? 0} câu hỏi
                                        </div>
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="relative dropdown-container flex-shrink-0">
                        <button
                          className="btn-primary flex items-center gap-2 px-5 py-2.5 text-sm w-full sm:w-auto justify-center shadow-lg hover:shadow-xl transition-shadow"
                          onClick={() => toggleClassExpansion(classRoom.id)}
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13 10V3L4 14h7v7l9-11h-7z"
                            />
                          </svg>
                          Tham gia
                          <svg
                            className={`w-4 h-4 transition-transform duration-200 ${expandedClasses[classRoom.id] ? "rotate-180" : "rotate-0"
                              }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Danh sách quiz trong lớp (Accordion) */}
                    {expandedClasses[classRoom.id] && (
                      <div
                        className="transition-all duration-300 ease-in-out overflow-hidden max-h-[1000px] opacity-100 mt-5 border-t border-gray-100 dark:border-gray-700 pt-5"
                      >
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                          <svg
                            className="w-5 h-5 text-primary-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                            />
                          </svg>
                          Bài kiểm tra trong lớp
                        </h4>

                        {loadingQuizzes[classRoom.id] ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                          </div>
                        ) : classQuizzes[classRoom.id] && classQuizzes[classRoom.id].length > 0 ? (
                          <div className="grid gap-3">
                            {(classQuizzes[classRoom.id] as Quiz[]).map((quiz) => (
                              <div
                                key={quiz.id}
                                className="group/quiz p-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-800/50 rounded-xl hover:shadow-lg transition-all duration-200 border border-gray-200 dark:border-gray-700"
                              >
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                  <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-gray-900 dark:text-white mb-1 group-hover/quiz:text-primary-600 dark:group-hover/quiz:text-primary-400 transition-colors">
                                      {quiz.title}
                                    </p>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                                      {quiz.description}
                                    </p>
                                  </div>
                                  <button
                                    onClick={() =>
                                      navigate(`/quiz/${quiz.id}`, {
                                        state: { className: classRoom.name },
                                      })
                                    }
                                    className="btn-secondary text-sm px-4 py-2 flex items-center justify-center gap-2 hover:bg-primary-500 hover:text-white transition-all"
                                  >
                                    <svg
                                      className="w-4 h-4"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                                      />
                                    </svg>
                                    Làm bài
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                            Chưa có bài kiểm tra nào
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Section - Sidebar */}
          <div className="w-full lg:w-[30%] lg:flex-shrink-0">
            <div className="lg:sticky lg:top-4">
              <div className="card p-4 sm:p-6 bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 border-0 animate-slideLeftIn anim-delay-200">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/30 mb-3">
                    <svg
                      className="w-6 h-6 text-primary-600 dark:text-primary-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                    Xem điểm LMS ICTU
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    <span style={{ color: "red", fontWeight: "bold" }}>Chú ý:</span> Dùng tài khoản LMS để đăng nhập🔥
                  </p>
                  <a
                    href="https://tuanhub.id.vn"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block text-sm font-mono text-primary-600 dark:text-primary-400 hover:underline break-all"
                  >
                    tuanhub.id.vn
                  </a>
                </div>

                <div className="flex items-center justify-center">
                  <div
                    className="perspective-1000 w-full"
                    onMouseMove={handleMouseMove1}
                    onMouseLeave={handleMouseLeave1}
                  >
                    <a
                      href="https://tuanhub.id.vn"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="relative group block"
                    >
                      <img
                        src={
                          isDarkMode
                            ? require("../assets/xemdiem.png")
                            : require("../assets/xemdiem.png")
                        }
                        alt={isDarkMode ? "CNTT K23M Logo" : "CNTT K23M Logo"}
                        className="w-full h-auto rounded-xl shadow-2xl transition-all duration-300 ease-out cursor-pointer hover:shadow-3xl"
                        style={{
                          maxHeight: 280,
                          transform: `perspective(1000px) rotateY(${mousePosition1.x * 0.1
                            }deg) rotateX(${-mousePosition1.y * 0.1
                            }deg) translateZ(${Math.abs(mousePosition1.x) +
                              Math.abs(mousePosition1.y) >
                              0
                              ? "20px"
                              : "0px"
                            })`,
                          border: "2px solid transparent",
                          backgroundImage: isDarkMode
                            ? "linear-gradient(45deg, #0ea5e9, #06b6d4, #10b981, #84cc16)"
                            : "linear-gradient(45deg, #3b82f6, #8b5cf6, #ec4899, #f59e0b)",
                          backgroundSize: "400% 400%",
                          animation: "neonBorder 3s ease-in-out infinite",
                          backgroundClip: "border-box",
                          borderRadius: "12px",
                        }}
                      />
                      {/* Tooltip */}
                      <div
                        className={`opacity-0 group-hover:opacity-100 pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-3 text-xs rounded-lg px-4 py-2 shadow-xl transition-opacity duration-200 z-20 whitespace-nowrap font-medium ${isDarkMode
                          ? "bg-gray-800 text-white border border-gray-700"
                          : "bg-white text-gray-900 border border-gray-200"
                          }`}
                      >
                        Click để chuyển đến trang →
                      </div>
                    </a>
                  </div>
                </div>
              </div>

              {/* Live Stream / Entertainment Card */}
              <div className="card p-4 sm:p-6 bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 border-0 mt-6 animate-slideLeftIn anim-delay-300">
                <div className="text-center mb-4">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 mb-3 animate-pulse">
                    <FaVideo size={24} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                    Giải Trí & Phim Ảnh
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Xem phim, bóng đá, sự kiện trực tiếp cùng nhau.
                  </p>
                  <div className="flex justify-center">
                    <button
                      onClick={() => navigate('/live')}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white font-bold rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                    >
                      <FaPlay size={12} />
                      <span>XEM NGAY</span>
                    </button>
                  </div>
                </div>
              </div>


            </div >
          </div >
        </div >
      </div>
    </>
  );
};

export default HomePage;
